import { Link } from '@tanstack/react-router';
import type { TodayState } from '@thrivehrm/shared';
import { StatTile } from '@/components/ui/stat-tile';
import { StatTilesSkeleton, WidgetError } from '@/features/dashboard/widget-states';
import { useAttendanceTodaySummary, useTeamToday } from './api';
import { TODAY_STATE_LABELS } from './labels';

const SHOWN: readonly TodayState[] = ['IN', 'WFH', 'ON_LEAVE', 'NOT_CHECKED_IN'];

function Counts({ counts }: { counts: Readonly<Record<TodayState, number>> }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {SHOWN.map((s) => (
        <StatTile key={s} label={TODAY_STATE_LABELS[s]} value={counts[s]} />
      ))}
    </div>
  );
}

/** Managers (T-07E, FR-ATT-11): where my reporting tree is today, linking to Team attendance. */
export function TeamTodayWidget() {
  const board = useTeamToday();
  if (board.isPending) return <StatTilesSkeleton count={4} />;
  if (board.error) {
    return <WidgetError onRetry={() => void board.refetch()} retrying={board.isRefetching} />;
  }
  const counts = Object.fromEntries(
    SHOWN.map((s) => [s, board.data.items.filter((i) => i.state === s).length]),
  ) as Record<TodayState, number>;
  return (
    <div className="flex flex-col gap-4">
      <Counts counts={counts} />
      <Link
        to="/team/attendance"
        className="w-fit text-small font-semibold text-primary hover:underline"
      >
        Open team attendance
      </Link>
    </div>
  );
}

/**
 * HR (T-07E, FR-ATT-11): company counts for today and — with attendance.manage — the flagged
 * punches and unmapped device users waiting for review.
 */
export function AttendanceTodayHrWidget() {
  const summary = useAttendanceTodaySummary();
  if (summary.isPending) return <StatTilesSkeleton count={4} />;
  if (summary.error) {
    return <WidgetError onRetry={() => void summary.refetch()} retrying={summary.isRefetching} />;
  }
  const { counts, total, flagged, unmapped } = summary.data;
  return (
    <div className="flex flex-col gap-4">
      <Counts counts={counts} />
      <p className="text-small text-muted-foreground">{total} on the roster today.</p>
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-small font-semibold">
        <Link to="/hr/attendance" className="text-primary hover:underline">
          Attendance register
        </Link>
        {flagged !== null && flagged > 0 ? (
          <Link to="/hr/attendance/flags" className="text-primary hover:underline">
            {flagged} flagged {flagged === 1 ? 'punch' : 'punches'} to review
          </Link>
        ) : null}
        {unmapped !== null && unmapped > 0 ? (
          <Link to="/hr/attendance/devices" className="text-primary hover:underline">
            {unmapped} unmapped device {unmapped === 1 ? 'user' : 'users'}
          </Link>
        ) : null}
      </div>
    </div>
  );
}
